import { CityDTO, SchoolCourseTypologyResDTO } from "@/generated/@habacus/hcp_api/src";
import { StepSchoolsInfo } from "@/types/configuration";
import { SelectData } from "@/types/typesInterfaces";
import { fetchCities, fetchSchoolCoursesTypologiesWithPaginate, fetchSchoolPropaedeuticDegree, fetchSchoolsWithPaginate } from "@/utils/client/client_api";
import { SchoolCourseResource } from "@/utils/dto/SchoolCoursesResource";
import { degreeTypeLabels, registrationYearOptionsFromDegreeType } from "@/utils/dto/SchoolCourseTypesResource";
import { SchoolResource } from "@/utils/dto/SchoolsResource";
import { selectOnSeach } from "@/utils/helpers";
import { Select } from "@habacuscode/habacus_ui_component_library";
import { useTranslations } from "next-intl";
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";

export interface SchoolAndCoursesSelectorProps {
  locale: string,
  school?: SchoolResource;
  course?: SchoolCourseTypologyResDTO;
  coursePrevious?: SchoolCourseResource;
  vote?: SelectData;
  year?: SelectData;
  residence?: CityDTO;
  fixedSchools?: StepSchoolsInfo;
  onChangeData: (e: any, name: string) => void;
}

export interface SchoolAndCoursesSelectorRef {
  validate: () => boolean;
}

const SchoolAndCoursesSelector = forwardRef<SchoolAndCoursesSelectorRef, SchoolAndCoursesSelectorProps>(({
  locale,
  school,
  course,
  coursePrevious,
  vote,
  year,
  residence,
  fixedSchools,
  onChangeData,
}: SchoolAndCoursesSelectorProps, ref) => {
  const tInput = useTranslations('Input');
  const tCommon = useTranslations('Common');
  const notInItaly: string = tInput("cities.notInItaly");
  const notInItalySearch: string[] = tInput("cities.notInItalySearch").toLowerCase().split(' ');

  // Initialize state for each checkbox
  const [selectedSchool, setSelectedSchool] = useState<SchoolResource | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<SchoolCourseTypologyResDTO | null>(null);
  const [selectedPropaedeuticDegrees, setSelectedPropaedeuticDegrees] = useState<SchoolCourseResource | null>(null);
  const [selectedVote, setSelectedVote] = useState<SelectData | null>();
  const [selecedYear, setSelectedYear] = useState<SelectData | null>();

  const [cities, setCities] = useState<CityDTO[]>([]);
  const [residenceCity, setResidenceCity] = useState<CityDTO | null>(null);
  const [residenceCityPage, setResidenceCityPage] = useState<number>(1);
  const [residenceCityTerm, setResidenceCityTerm] = useState<string>();
  const [residenceCityTotalCount, setResidenceCityTotalCount] = useState<number>();


  const [loading, setLoading] = useState(false);

  const [searchTermSchool, setSearchTermSchool] = useState<string>('');
  const [pageSchool, setPageSchool] = useState<number>(1);
  const [schoolsTotalCount, setSchoolsTotalCount] = useState<number>();
  const [schools, setSchools] = useState<SchoolResource[]>([]);
  const [coursesTypologies, setCoursesTypologies] = useState<SchoolCourseTypologyResDTO[]>([]);
  const [filteredCoursesTypologies, setFilteredCoursesTypologies] = useState<SchoolCourseTypologyResDTO[]>([]);
  const [propaedeuticDegrees, setPropaedeuticDegrees] = useState<SchoolCourseResource[]>([]);
  const [availableVotes, setAvailableVotes] = useState<SelectData[]>([]);
  const [availableYears, setAvailableYears] = useState<SelectData[]>();
  const [filteredAvailableVotes, setFilteredAvailableVotes] = useState<Array<SelectData>>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    // residence (cities)
    if (!residenceCity || !residenceCity.code) {
      newErrors["residence"] = tCommon("validation.error.valueMissing");
    }
    // school
    if (!selectedSchool || !(selectedSchool.uuid || selectedSchool.name)) {
      newErrors["school"] = tCommon("validation.error.valueMissing");
    }
    // course
    if (!selectedCourse || !selectedCourse.id) {
      newErrors["academicQualification"] = tCommon("validation.error.valueMissing");
    }

    // anno attuale
    if (availableYears && availableYears.length > 0 && !selecedYear) {
      newErrors["currentYear"] = tCommon("validation.error.valueMissing");
    }

    // previous course (when list exists we expect a choice; default is auto-selected when available)
    if (propaedeuticDegrees.length > 0 && (!selectedPropaedeuticDegrees || !selectedPropaedeuticDegrees.id)) {
      newErrors["previousCourse"] = tCommon("validation.error.valueMissing");
    }
    // vote
    if (
      selectedPropaedeuticDegrees &&
      (!selectedVote ||
        selectedVote.value === undefined ||
        selectedVote.value === null)
    ) {
      newErrors["previusVote"] = tCommon("validation.error.valueMissing");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({ validate }));

  // on init call get schools empty to see some 
  useEffect(() => {
    setPageSchool(1)
    callGetCities("");
  }, [])

  useEffect(() => {
    if (fixedSchools && fixedSchools.schoolUuids) {
      callGetSchools('')
    }
  }, [fixedSchools])

  useEffect(() => {
    console.log("residence init", residence)
    if (residence) {
      const filtered = cities.filter(el => el.name!.toLowerCase().indexOf(residence.name!.toLowerCase()) !== -1)
      console.log("filtered", filtered)
      setCities(filtered.length > 0 ? filtered : [{ name: tCommon('noOptionsMessage'), code: undefined }])
      setResidenceCity(residence);
    }
  }, [residence])

  useEffect(() => {
    console.log("school init", school)
    if (school) {
      setSearchTermSchool(school.name);
      onChangeData(school, "school");
      setSelectedSchool(school);
    }
  }, [school])

  useEffect(() => {
    console.log("course init", course)
    if (course) {
      setAvailableYears(undefined);
      const years: Array<SelectData> = calcAvailableYears(course);
      setAvailableYears(years);
      if (year) {
        onChangeYear(year);
      }

      callGetSchoolCourses(course.name!);
      onChangeData(course, "course");
      setSelectedCourse(course);
    }
  }, [course])

  useEffect(() => {
    console.log("vote init", vote)
    if (vote) {
      setSelectedVote(vote!);
      onChangeData(vote, "vote")
    }
  }, [vote])

  useEffect(() => {
    callGetCities(residenceCityTerm);
  }, [residenceCityPage, residenceCityTerm]);

  useEffect(() => {
    callGetSchools(searchTermSchool);
  }, [pageSchool, searchTermSchool]);

  // on change school call the courses of the school
  useEffect(() => {
    if (selectedSchool != null) {
      callGetSchoolCourses('');
    }
  }, [selectedSchool])

  // on change course call the PropaedeuticDegree
  useEffect(() => {
    if (selectedCourse != null) {
      callFetchSchoolPropaedeuticDegree('')
    }
  }, [selectedCourse])

  // when arrives propaedeuticDegrees  
  useEffect(() => {
    if (propaedeuticDegrees && propaedeuticDegrees.length > 0) {
      setSelectedPropaedeuticDegrees(propaedeuticDegrees[0])
      onChangeData(propaedeuticDegrees[0], "course_previous");

      // TODO andrebbero recuperati da HCP (School::getMaxMinGradesByCourseType)
      const min = propaedeuticDegrees[0].id == 'HS' ? 60 : 66;
      const max = propaedeuticDegrees[0].id == 'HS' ? 100 : 110;
      const votes: SelectData[] = Array.from({ length: max - min + 1 }, (_, i) => {
        return {
          "value": i + min,
          "label": (i + min).toString(),
        }
      }).sort(function (a, b) { return b.value - a.value; });
      votes.unshift({
        "value": votes[0].value,
        "label": tInput("previusVote.cumLaude", { vote: votes[0].value! }),
        "extra": {
          "isCumLaude": true
        }
      })
      votes.unshift({
        "value": -1,
        "label": tInput("previusVote.toBeAchieved"),
      })

      setAvailableVotes(votes)
      setFilteredAvailableVotes(votes)
    }
  }, [propaedeuticDegrees])

  const getTitleDiploma = (): string => {
    return `${tInput('previusVote.label')} ${selectedPropaedeuticDegrees?.name}`;
  }

  const callGetCities = async (filter: string = '') => {
    const reqOptions = {
      page: residenceCityPage,
      perPage: 100,
      lang: locale,
      query: filter,
    };

    const res = await fetchCities({
      country: 'italia',
      ...reqOptions,
    });

    const data: CityDTO[] = res.response?.data || [];

    if (!filter
      || data.length == 0
      || notInItalySearch.some((word: string) => word.includes(filter.toLocaleLowerCase()))) {
      data.unshift({
        code: notInItaly,
        name: notInItaly
      });
    }

    if (residenceCityPage === 1) {
      setCities(data)
    } else {
      setCities((prev) => [...prev, ...data]);
    }

    console.log("*** call get courses RES ", data);

    setLoading(false);
  }

  const callGetSchools = async (filter: string = '') => {
    if (schoolsTotalCount && schools.length >= schoolsTotalCount) {
      return;
    }

    setLoading(true);
    console.log(`*** call get schools... term="${filter}" page=${pageSchool}`);

    const res = await fetchSchoolsWithPaginate({
      page: pageSchool,
      per_page: 50,
      query_search: filter,
      uuids: fixedSchools?.schoolUuids,
    });

    console.log("*** call get schools RES ", res);
    if (pageSchool === 1) {
      setSchools(res?.response?.schools || []);
    } else {
      setSchools((prev) => [...prev, ...(res?.response?.schools || [])]);
      console.log("numero scuole: ", schools.length)
    }

    if (fixedSchools?.schoolUuids && fixedSchools.schoolUuids.length === 1
      && res?.response?.schools
    ) {
      onChangeSchool(res?.response?.schools[0])
    }

    setSchoolsTotalCount(res?.response?.totalCount || 0);
    setLoading(false);
  };

  const callGetSchoolCourses = async (term: string) => {
    //TODO - usa la api in questa func schoolCourseTypologiesRaw per filtrare i corsi relativi alla school
    // scelta prima
    setLoading(true);

    console.log("*** call get courses...");

    let schoolCourseTypologies: SchoolCourseTypologyResDTO[] = [];

    if (fixedSchools?.customSchoolCourseTypologies && fixedSchools.customSchoolCourseTypologies.length > 0) {
      schoolCourseTypologies = fixedSchools.customSchoolCourseTypologies.map((customSchoolCourseTypology) => {
        return {
          id: customSchoolCourseTypology.value,
          value: customSchoolCourseTypology.value,
          name: customSchoolCourseTypology.name[locale],
          schoolCourseTypologyDurations: [],
        }
      });
    } else {

      // dovresti poter usare school id scelto x filtrare i corsi possibili
      const res = await fetchSchoolCoursesTypologiesWithPaginate({
        page: 1,
        perPage: 100,
        schoolTypologyName: selectedSchool?.typology,
        schoolUuid: selectedSchool?.uuid,
        schoolCourseTypologies: fixedSchools?.schoolCourseTypologies?.join(','),
        query: term,
      })

      console.log("*** call get courses RES ", res);
      schoolCourseTypologies = res.response?.schoolCourseTypologies || [];
      schoolCourseTypologies.sort((a: SchoolCourseTypologyResDTO, b: SchoolCourseTypologyResDTO) => { return (a.name!.toLowerCase()) >= (b.name!.toLowerCase()) ? 1 : -1; });
    }
    setCoursesTypologies(schoolCourseTypologies)
    setFilteredCoursesTypologies(schoolCourseTypologies)

    // if (fixedSchools?.schoolCourseTypologies && fixedSchools.schoolCourseTypologies.length === 1
    //   && schoolCourseTypologies
    // ) {
    //   onChangeCourse(schoolCourseTypologies[0])
    // }
    setLoading(false);
  };

  const callFetchSchoolPropaedeuticDegree = async (term: string) => {
    //TODO - usa la api in questa func schoolCourseTypologiesRaw per filtrare i corsi relativi alla school
    // scelta prima
    setLoading(true);

    console.log("*** call get degrees...");
    // dovresti poter usare school id scelto x filtrare i corsi possibili
    const res = await fetchSchoolPropaedeuticDegree({
      courseType: selectedCourse?.id ?? '',
    })
    console.log("*** call get degrees RES ", res);
    res.response?.schoolCourses.map((schoolCourse) => {
      schoolCourse.name = degreeTypeLabels[schoolCourse.id as keyof typeof degreeTypeLabels];
    });
    setPropaedeuticDegrees(res.response?.schoolCourses ?? [])
    setLoading(false);
  };

  const calcAvailableYears = (course: SchoolCourseTypologyResDTO): Array<SelectData> => {
    let years: SelectData[] = [];
    const yearOptions: { min: number, max: number } | undefined = registrationYearOptionsFromDegreeType[course.id as keyof typeof degreeTypeLabels];
    if (yearOptions) {
      let ordinalIndicator = tInput.raw(`currentYear.ordinalIndicator`);

      years = Array.from({ length: yearOptions.max + 1 }, (_, i) => {
        if (i == 0) {
          return {
            "value": i,
            "label": tInput("currentYear.notRegistered")
          }
        }

        return {
          "value": i,
          "label": tInput("currentYear.year", {
            year: i, ordinalIndicator: ordinalIndicator[i] ?? ordinalIndicator.other
          })
        }
      });
    }

    return years;
  }

  const onChangeCity = (city: CityDTO): void => {
    onChangeData(city, "city")
    setResidenceCity(city);
  }

  const onChangeSchool = (school: SchoolResource): void => {
    onChangeData(school, "school")
    setSelectedSchool(school);

    onChangeCourse(null);
  }

  const onChangeCourse = (course: SchoolCourseTypologyResDTO | null): void => {
    setAvailableYears(undefined);
    if (course) {
      const years: Array<SelectData> = calcAvailableYears(course);
      setAvailableYears(years);
      if (year) {
        onChangeYear(year);
      }
    }

    onChangeData(course, "course");
    setSelectedCourse(course);

    onChangeYear(null)
    onChangeCoursePrevious(null);
  }

  const onChangeYear = (year: SelectData | null): void => {
    setSelectedYear(year);
    onChangeData(year, "year_actual")
  }

  const onChangeCoursePrevious = (propaedeuticDegree: SchoolCourseResource | null): void => {
    onChangeData(propaedeuticDegree, "course_previous");
    setSelectedPropaedeuticDegrees(propaedeuticDegree);

    onChangeVote(null);
  }

  const onChangeVote = (vote: SelectData | null): void => {
    setSelectedVote(vote);
    onChangeData(vote, "vote");
  }

  return (
    <div className="w-full ">
      <div className="space-y-4">
        <div className="flex flex-col">
          <div className="w-full">
            <div className="pb-6">
              <Select
                id="cities"
                name="cities"
                key="search_cities"
                searchable={true}
                loading={loading}
                label={tInput("cities.label")}
                placeholder={tInput("cities.placeholder")}
                searchPlaceholder={tInput("cities.searchPlaceholder")}
                onSearch={(e: any) => {
                  setResidenceCityPage(1);
                  setResidenceCityTerm(e.toString());
                  setResidenceCityTotalCount(0);
                }}
                onScroll={() => {
                  setResidenceCityPage((prevPage) => prevPage + 1);
                }}
                onChange={(city: CityDTO) => {
                  onChangeCity(city);
                  setErrors((prev) => ({ ...prev, cities: '' }));
                }}
                defaultValue={residence}
                selectFirstValue={false}
                items={cities}
                idKey="code"
                labelKey="name"
                required={true}
                error={errors['residence']}
              />
            </div>

            {schools && (
              <div className="pb-6">
                <Select
                  id="school"
                  name="school"
                  key={`search_school`}
                  searchable={true}
                  loading={loading}
                  label={tInput("school.label")}
                  placeholder={tInput("school.placeholder")}
                  searchPlaceholder={tInput("school.searchPlaceholder")}
                  onSearch={(e: SchoolResource) => {
                    setPageSchool(1);
                    setSearchTermSchool(e.toString());
                    setSchoolsTotalCount(0);
                  }}
                  onScroll={() => {
                    setPageSchool((prevPage) => prevPage + 1);
                  }}
                  onChange={(school: SchoolResource) => {
                    onChangeSchool(school);
                    setErrors((prev) => ({ ...prev, school: '' }));
                  }}
                  defaultValue={school}
                  selectFirstValue={false}
                  items={schools}
                  idKey="uuid"
                  labelKey="name"
                  required={true}
                  error={errors['school']}
                />
              </div>
            )}
          </div>

          {selectedSchool && (
            <div className={`pb-6 ${availableYears && availableYears.length > 0 ? "grid md:grid-cols-[2fr_1fr] gap-2" : ""}`}>
              <Select
                id="titolo_studio"
                name="titolo_studio"
                key={`search_titolostudio`}
                searchable={true}
                loading={loading}
                label={tInput("academicQualification.label")}
                placeholder={tInput("academicQualification.placeholder")}
                searchPlaceholder={tInput("academicQualification.searchPlaceholder")}
                onSearch={(e: SchoolCourseTypologyResDTO) => {
                  const filtered = selectOnSeach(e, coursesTypologies, (e: SchoolCourseTypologyResDTO) => {
                    onChangeCourse(e);
                  }, "name");
                  setFilteredCoursesTypologies(filtered);
                }}
                onChange={(course: SchoolCourseTypologyResDTO) => {
                  onChangeCourse(course);
                  setErrors((prev) => {
                    const { academicQualification, ...rest } = prev;
                    return rest;
                  });
                }}
                defaultValue={course}
                selectFirstValue={false}
                items={filteredCoursesTypologies}
                idKey="id"
                labelKey="name"
                required={true}
                error={errors['academicQualification']}
              />

              {availableYears && availableYears.length > 0 && (
                <div className="pt-4 md:pt-0">
                  <Select
                    id="anno_studio"
                    name="anno_studio"
                    key={`search_anno_studio`}
                    searchable={true}
                    loading={loading}
                    label={tInput("currentYear.label")}
                    placeholder={tInput("currentYear.placeholder")}
                    onChange={(currentYear: SelectData) => {
                      onChangeYear(currentYear);
                      setErrors((prev) => ({ ...prev, currentYear: '' }));
                    }}
                    defaultValue={year}
                    selectFirstValue={false}
                    items={availableYears}
                    idKey="value"
                    labelKey="label"
                    required={true}
                    error={errors['currentYear']}
                  />
                </div>
              )}
            </div>
          )}

          {selectedCourse && propaedeuticDegrees?.length > 1 && (<div className="pb-6">
            <Select
              id="corso_precendente"
              name="corso_precendente"
              key={`search_corsoprecendente`}
              searchable={false}
              loading={loading}
              label={tInput("previousCourse.label")}
              placeholder={tInput("previousCourse.placeholder")}
              searchPlaceholder={tInput("previousCourse.searchPlaceholder")}
              onChange={(propaedeuticDegree: SchoolCourseResource) => {
                onChangeCoursePrevious(propaedeuticDegree);
                setErrors((prev) => ({ ...prev, previousCourse: '' }));
              }}
              defaultValue={coursePrevious}
              selectFirstValue={false}
              items={propaedeuticDegrees}
              idKey="id"
              labelKey="name"
              required={true}
              error={errors['previousCourse']}
            />
          </div>)}

          {selectedPropaedeuticDegrees && (<div className="pb-0 md:pb-6">
            <Select
              id="votes"
              name="votes"
              key={"search_votes"}
              searchable={true}
              loading={loading}
              label={getTitleDiploma()}
              placeholder={tInput("previusVote.placeholder")}
              searchPlaceholder={tInput("previusVote.searchPlaceholder")}
              onSearch={(e: SelectData) => {
                const filtered = selectOnSeach(e, availableVotes, (e) => {
                  onChangeVote(e);
                });
                setFilteredAvailableVotes(filtered);
              }}
              onChange={(vote: SelectData) => {
                onChangeVote(vote);
                setErrors((prev) => ({ ...prev, previusVote: '' }));
              }}
              defaultValue={vote}
              selectFirstValue={false}
              items={filteredAvailableVotes}
              idKey="value"
              labelKey="label"
              required={true}
              error={errors['previusVote']}
            />
          </div>
          )}
        </div>
      </div>
    </div>
  );
});

SchoolAndCoursesSelector.displayName = "SchoolAndCoursesSelector";
export default SchoolAndCoursesSelector;
